import React, { useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { StepIndicator } from '../components/StepIndicator';
import { UploadIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const STEPS = ['Master Creative', 'Campaign Setup', 'Select Creative', 'Styling Template', 'Variable Content', 'Generate', 'Review & Publish'];
export function VariableContent() {
  const navigate = useNavigate();
  const [pricingMode, setPricingMode] = useState<'same' | 'regional' | 'csv'>('same');
  const [offerMode, setOfferMode] = useState<'same' | 'regional' | 'csv'>('same');
  const [festivalMode, setFestivalMode] = useState<'same' | 'regional' | 'csv'>('same');
  const [pricing, setPricing] = useState('');
  const [offer, setOffer] = useState('');
  const [festival, setFestival] = useState('');
  const canContinue = pricing || offer || festival;
  return <div className="flex w-full min-h-screen bg-gray-50" data-id="element-2737">
      <Sidebar data-id="element-2738" />
      <main className="flex-1" data-id="element-2739">
        <Header data-id="element-2740" />
        <StepIndicator currentStep={5} steps={STEPS} data-id="element-2741" />
        <div className="px-6 py-8" data-id="element-2742">
          <div className="max-w-4xl mx-auto" data-id="element-2743">
            <h1 className="text-2xl font-semibold text-gray-800 mb-2" data-id="element-2744">
              Step 4: Fill Variable Content
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-2745">
              Configure pricing, offers, and festival text for your campaigns
            </p>

            <div className="space-y-6" data-id="element-2746">
              {/* State-wise Pricing */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2747">
                <h3 className="font-medium text-gray-800 mb-4" data-id="element-2748">
                  A. State-wise Pricing
                </h3>
                <div className="flex space-x-3 mb-4" data-id="element-2749">
                  <button onClick={() => setPricingMode('same')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${pricingMode === 'same' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2750">
                    Same for All
                  </button>
                  <button onClick={() => setPricingMode('regional')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${pricingMode === 'regional' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2751">
                    Region-wise
                  </button>
                  <button onClick={() => setPricingMode('csv')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${pricingMode === 'csv' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2752">
                    Upload CSV
                  </button>
                </div>

                {pricingMode === 'same' && <input type="text" value={pricing} onChange={e => setPricing(e.target.value)} placeholder="e.g., Starting at ₹12,50,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2753" />}

                {pricingMode === 'regional' && <div className="space-y-3" data-id="element-2754">
                    <div className="grid grid-cols-2 gap-3" data-id="element-2755">
                      <div data-id="element-2756">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-2757">
                          North Zone
                        </label>
                        <input type="text" placeholder="₹12,50,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2758" />
                      </div>
                      <div data-id="element-2759">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-2760">
                          South Zone
                        </label>
                        <input type="text" placeholder="₹12,75,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2761" />
                      </div>
                      <div data-id="element-2762">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-2763">
                          East Zone
                        </label>
                        <input type="text" placeholder="₹12,60,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2764" />
                      </div>
                      <div data-id="element-2765">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-2766">
                          West Zone
                        </label>
                        <input type="text" placeholder="₹12,80,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2767" />
                      </div>
                    </div>
                  </div>}

                {pricingMode === 'csv' && <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-2768">
                    <UploadIcon className="mx-auto text-gray-400 mb-3" size={32} data-id="element-2769" />
                    <p className="text-sm text-gray-600 mb-3" data-id="element-2770">
                      Upload CSV with dealer-specific pricing
                    </p>
                    <label className="inline-block bg-blue-500 text-white px-4 py-2 rounded-md text-sm cursor-pointer hover:bg-blue-600" data-id="element-2771">
                      Choose CSV File
                      <input type="file" accept=".csv" className="hidden" data-id="element-2772" />
                    </label>
                    <p className="text-xs text-gray-500 mt-2" data-id="element-2773">
                      Format: dealer_id, pricing
                    </p>
                  </div>}
              </div>

              {/* Offer/Discount Text */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2774">
                <h3 className="font-medium text-gray-800 mb-4" data-id="element-2775">
                  B. Offer/Discount Text
                </h3>
                <div className="flex space-x-3 mb-4" data-id="element-2776">
                  <button onClick={() => setOfferMode('same')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${offerMode === 'same' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2777">
                    Same for All
                  </button>
                  <button onClick={() => setOfferMode('regional')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${offerMode === 'regional' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2778">
                    Region-wise
                  </button>
                  <button onClick={() => setOfferMode('csv')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${offerMode === 'csv' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2779">
                    Dealer-specific CSV
                  </button>
                </div>

                {offerMode === 'same' && <textarea value={offer} onChange={e => setOffer(e.target.value)} placeholder="e.g., Get ₹50,000 off + Free accessories worth ₹25,000" rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2780" />}

                {offerMode === 'regional' && <div className="space-y-3" data-id="element-2781">
                    {['North', 'South', 'East', 'West'].map(zone => <div key={zone} data-id="element-2782">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-2783">
                          {zone} Zone Offer
                        </label>
                        <input type="text" placeholder={`Special offer for ${zone} zone`} className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2784" />
                      </div>)}
                  </div>}

                {offerMode === 'csv' && <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-2785">
                    <UploadIcon className="mx-auto text-gray-400 mb-3" size={32} data-id="element-2786" />
                    <p className="text-sm text-gray-600 mb-3" data-id="element-2787">
                      Upload CSV with dealer-specific offers
                    </p>
                    <label className="inline-block bg-blue-500 text-white px-4 py-2 rounded-md text-sm cursor-pointer hover:bg-blue-600" data-id="element-2788">
                      Choose CSV File
                      <input type="file" accept=".csv" className="hidden" data-id="element-2789" />
                    </label>
                  </div>}
              </div>

              {/* Festival Text */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2790">
                <h3 className="font-medium text-gray-800 mb-4" data-id="element-2791">
                  C. Festival Text
                </h3>
                <div className="flex space-x-3 mb-4" data-id="element-2792">
                  <button onClick={() => setFestivalMode('same')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${festivalMode === 'same' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2793">
                    Same for All
                  </button>
                  <button onClick={() => setFestivalMode('regional')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${festivalMode === 'regional' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2794">
                    Regional Mapping
                  </button>
                  <button onClick={() => setFestivalMode('csv')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${festivalMode === 'csv' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2795">
                    Dealer-specific CSV
                  </button>
                </div>

                {festivalMode === 'same' && <input type="text" value={festival} onChange={e => setFestival(e.target.value)} placeholder="e.g., Diwali Special Offer" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2796" />}

                {festivalMode === 'regional' && <div className="space-y-3" data-id="element-2797">
                    <div data-id="element-2798">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-2799">
                        North India
                      </label>
                      <input type="text" placeholder="e.g., Diwali Dhamaka" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2800" />
                    </div>
                    <div data-id="element-2801">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-2802">
                        South India
                      </label>
                      <input type="text" placeholder="e.g., Pongal Special" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2803" />
                    </div>
                    <div data-id="element-2804">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-2805">
                        East India
                      </label>
                      <input type="text" placeholder="e.g., Durga Puja Offer" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2806" />
                    </div>
                    <div data-id="element-2807">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-2808">
                        West India
                      </label>
                      <input type="text" placeholder="e.g., Ganesh Chaturthi Special" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2809" />
                    </div>
                  </div>}

                {festivalMode === 'csv' && <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-2810">
                    <UploadIcon className="mx-auto text-gray-400 mb-3" size={32} data-id="element-2811" />
                    <p className="text-sm text-gray-600 mb-3" data-id="element-2812">
                      Upload CSV with dealer-specific festival text
                    </p>
                    <label className="inline-block bg-blue-500 text-white px-4 py-2 rounded-md text-sm cursor-pointer hover:bg-blue-600" data-id="element-2813">
                      Choose CSV File
                      <input type="file" accept=".csv" className="hidden" data-id="element-2814" />
                    </label>
                  </div>}
              </div>
            </div>

            <div className="flex justify-between mt-6" data-id="element-2815">
              <button onClick={() => navigate('/dealerization/step-3')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-2816">
                Back
              </button>
              <button onClick={() => navigate('/dealerization/step-5')} disabled={!canContinue} className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-2817">
                Continue to Generate Creatives
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>;
}